import type { ColorAttribute } from '../helpers';
import { findOptionIgnoringCase, sameCaseInsensitive } from './casing';
import type { RepairContext, StoreOption, VariantMappingRepairStrategy } from './types';

const words = (label: string) => label.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];

/** Match whole words in either direction, including punctuation changes; never Red -> Redwood or 2 -> 20. */
const hasMatchingWords = (before: string, after: string) => {
    const original = words(before);
    const candidate = words(after);
    const [shorter, longer] = original.length <= candidate.length ? [original, candidate] : [candidate, original];
    return shorter.length > 0 && longer.some((_, start) => shorter.every((word, offset) => word === longer[start + offset]));
};

const findRenamedValue = (attribute: ColorAttribute, values: StoreOption['values']) => {
    const sameID = values.filter((value) => value.valueID === attribute.Value.Id);
    if (sameID.length) return sameID.length === 1 ? sameID[0] : null;
    const sameName = values.filter((value) => sameCaseInsensitive(value.label, attribute.Value.Label));
    if (sameName.length) return sameName.length === 1 ? sameName[0] : null;
    const related = values.filter((value) => hasMatchingWords(attribute.Value.Label, value.label));
    return related.length === 1 ? related[0] : null;
};

const findAttributeOption = (attribute: ColorAttribute, { options, isShopify }: RepairContext) => findOptionIgnoringCase(attribute, options, !isShopify);

/** A local candidate; channel-wide uniqueness must be checked before presenting or applying it. */
export const suggestRenamedAttribute = (attribute: ColorAttribute, context: RepairContext): ColorAttribute | null => {
    const option = findAttributeOption(attribute, context);
    const value = option && findRenamedValue(attribute, option.values);
    return option && value
        ? { ...attribute, Id: option.optionID, Label: option.label, Value: { ...attribute.Value, Id: value.valueID, Label: value.label } }
        : null;
};

export const renamedValuesStrategy: VariantMappingRepairStrategy = {
    id: 'value-rename',
    isApplicable: () => true,
    repair: (attributes, context) =>
        attributes.map((attribute) => {
            const suggestion = suggestRenamedAttribute(attribute, context);
            // Leave case-only changes to the casing strategy so the preview explains them accurately.
            if (
                !suggestion ||
                (sameCaseInsensitive(suggestion.Value.Id, attribute.Value.Id) && sameCaseInsensitive(suggestion.Value.Label, attribute.Value.Label))
            )
                return attribute;
            return { ...attribute, Value: suggestion.Value };
        })
};
